import { useEffect, useRef, useState } from 'react';

function videoClock(seconds: number) {
  if (!Number.isFinite(seconds) || seconds < 0) return '0:00';
  const whole = Math.floor(seconds);
  const hours = Math.floor(whole / 3600);
  const minutes = Math.floor((whole % 3600) / 60);
  const remainder = String(whole % 60).padStart(2, '0');
  return hours ? `${hours}:${String(minutes).padStart(2, '0')}:${remainder}` : `${minutes}:${remainder}`;
}

/**
 * Private review player, not DRM.  It deliberately does not render Safari's native
 * video controls or a media-download action.  The Worker independently forbids
 * participant feature-media download dispositions; it still has to stream the
 * inline media bytes to the browser for playback, which can be copied.
 */
export function PrivateReviewVideo({ src, title, eager = false, className = '' }: { src: string; title: string; eager?: boolean; className?: string }) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const shellRef = useRef<HTMLDivElement>(null);
  const [playing, setPlaying] = useState(false);
  const [started, setStarted] = useState(false);
  const [buffering, setBuffering] = useState(false);
  const [muted, setMuted] = useState(false);
  const [volume, setVolume] = useState(1);
  const [elapsed, setElapsed] = useState(0);
  const [duration, setDuration] = useState(0);
  const [fullscreen, setFullscreen] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setPlaying(false);
    setStarted(false);
    setBuffering(false);
    setElapsed(0);
    setDuration(0);
    setError('');
  }, [src]);

  useEffect(() => {
    const sync = () => setFullscreen(document.fullscreenElement === shellRef.current);
    document.addEventListener('fullscreenchange', sync);
    return () => document.removeEventListener('fullscreenchange', sync);
  }, []);

  async function togglePlay() {
    const video = videoRef.current;
    if (!video) return;
    if (!video.paused) {
      video.pause();
      return;
    }
    setError('');
    try {
      await video.play();
    } catch {
      setBuffering(false);
      setError('Video playback could not start. Try again or refresh the page.');
    }
  }

  function seekTo(next: number) {
    const video = videoRef.current;
    if (!video || !Number.isFinite(next) || !Number.isFinite(video.duration)) return;
    const safe = Math.max(0, Math.min(next, video.duration));
    video.currentTime = safe;
    setElapsed(safe);
  }

  function toggleMute() {
    const video = videoRef.current;
    if (!video) return;
    video.muted = !video.muted;
    setMuted(video.muted);
  }

  function adjustVolume(next: number) {
    const video = videoRef.current;
    if (!video) return;
    const safe = Math.max(0, Math.min(1, next));
    video.volume = safe;
    video.muted = safe === 0;
    setVolume(safe);
    setMuted(video.muted);
  }

  async function toggleFullscreen() {
    const shell = shellRef.current;
    if (!shell) return;
    try {
      if (document.fullscreenElement === shell) await document.exitFullscreen();
      else if (shell.requestFullscreen) await shell.requestFullscreen();
      else setError('Fullscreen is not available in this browser.');
    } catch {
      setError('Fullscreen is not available in this browser.');
    }
  }

  return <div ref={shellRef} className={`roam-review-player protected-video-shell ${className}`} onContextMenu={(event) => event.preventDefault()}>
    <video
      ref={videoRef}
      className="protected-video"
      src={src}
      aria-label={title}
      preload={eager ? 'auto' : 'metadata'}
      playsInline
      controls={false}
      disablePictureInPicture
      disableRemotePlayback
      onPlay={() => { setPlaying(true); setStarted(true); }}
      onPlaying={() => { setPlaying(true); setBuffering(false); }}
      onPause={() => { setPlaying(false); setBuffering(false); }}
      onWaiting={() => { if (started) setBuffering(true); }}
      onStalled={() => { if (started) setBuffering(true); }}
      onEnded={() => { setPlaying(false); setBuffering(false); }}
      onTimeUpdate={(event) => setElapsed(event.currentTarget.currentTime)}
      onDurationChange={(event) => {
        const next = event.currentTarget.duration;
        setDuration(Number.isFinite(next) ? next : 0);
      }}
      onVolumeChange={(event) => {
        setMuted(event.currentTarget.muted);
        setVolume(event.currentTarget.volume);
      }}
      onError={() => {
        setPlaying(false);
        setBuffering(false);
        setError('We could not load this private video. Please refresh or contact ROAM.');
      }}
    />
    <div className="roam-review-player__mark" aria-hidden="true">ROAM · PRIVATE REVIEW</div>
    <button type="button" className="roam-review-player__surface" onClick={() => { void togglePlay(); }} aria-label={playing ? 'Pause video' : 'Play video'}>
      {!playing && <span className="roam-review-player__big-play" aria-hidden="true">▶</span>}
    </button>
    {buffering && started && <div className="video-buffering-notice" role="status">Buffering video…</div>}
    {error && <div className="roam-review-player__error" role="alert">{error}</div>}
    <div className="roam-review-player__controls" role="group" aria-label="Video playback controls">
      <button type="button" onClick={() => { void togglePlay(); }} aria-label={playing ? 'Pause' : 'Play'}>{playing ? '❚❚' : '▶'}</button>
      <button type="button" onClick={() => seekTo(elapsed - 15)} aria-label="Go back 15 seconds">−15</button>
      <span className="roam-review-player__clock" aria-label={`Position ${videoClock(elapsed)}`}>{videoClock(elapsed)}</span>
      <input type="range" min="0" max={duration || 0} value={Math.min(elapsed, duration || 0)} step="0.1" disabled={!duration} onChange={(event) => seekTo(Number(event.target.value))} aria-label="Seek video" />
      <span className="roam-review-player__clock" aria-label={`Duration ${videoClock(duration)}`}>{videoClock(duration)}</span>
      <button type="button" onClick={toggleMute} aria-label={muted ? 'Unmute' : 'Mute'}>{muted ? 'Muted' : 'Sound'}</button>
      <input className="roam-review-player__volume" type="range" min="0" max="1" step="0.05" value={muted ? 0 : volume} onChange={(event) => adjustVolume(Number(event.target.value))} aria-label="Volume" />
      <button type="button" onClick={() => { void toggleFullscreen(); }} aria-label={fullscreen ? 'Exit fullscreen' : 'Fullscreen'}>{fullscreen ? 'Exit full' : 'Full'}</button>
    </div>
  </div>;
}
